import { CASTLE_MASK, TYPE_MASK, colorBit, moveFrom, movePromo, parseSquare, squareName } from '../board.ts';
import { FenError } from '../notation.ts';
import type { MoveContext } from '../rules.ts';
import type { Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 14 (draft). Captures are contagious. Capturing a piece doesn't remove it: it switches
// to your side where it stands, and your piece stays where it was (Othello-style). Kings
// can't be captured, so check and checkmate work as usual. Nothing ever leaves the board.
// No en passant (the pawn wouldn't go anywhere). A pawn that converts a piece on the last
// rank stays a pawn, so of its four promotion moves only the first is kept.
// Ko rule (as in Go): the piece that just switched sides can't be switched straight back,
// otherwise two attackers could flip the same piece back and forth forever.
// A king can only convert a piece it could take in normal chess: one that isn't protected.
// (It never moves, so otherwise a queen giving check next to it was a free gift.)
//
// ext: [square of the piece converted by the last move + 1, or 0]

const KO = 0;

export const FLIP_FEN = `${STANDARD_FEN} -`;

/** The square that can't be captured this move (ko), or -1. */
export function koSquare(pos: { ext: Int32Array | null }): number {
  return (pos.ext?.[KO] ?? 0) - 1;
}

export const flip: Variant = {
  id: 'flip',
  info: {
    name: 'Flip Chess',
    but: 'captures are contagious',
    tagline: 'Capture a piece and it joins your side. Nothing ever leaves the board.',
    summary: 'Capturing doesn’t remove a piece: it **switches to your side** where it stands, and your piece stays put.',
    rules: [
      'When you capture a piece, it switches to your colour on its square. Your capturing piece stays where it was.',
      'Kings can’t be captured, so check and checkmate work as usual.',
      'A king can only convert a piece that isn’t protected, just like a normal king capture.',
      'A pawn that converts a piece doesn’t move, so it doesn’t promote.',
      'Ko: the piece that just switched sides can’t be switched straight back.',
      'No en passant. Stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#5fcf7a',
    emoji: '🔄',
    bots: ['Coin Flip', 'Turncoat', 'Recruiter', 'Converter', 'Mastermind'],
  },
  startFen: FLIP_FEN,
  enPassant: false,
  extSize: 1,

  parseExtra(fields, pos) {
    const [ko = '-'] = fields;
    const sq = ko === '-' ? -1 : parseSquare(ko);
    if (ko !== '-' && sq < 0) throw new FenError('Bad ko square');
    pos.ext![KO] = sq + 1;
  },

  formatExtra(pos) {
    const sq = koSquare(pos);
    return [sq >= 0 ? squareName(sq) : '-'];
  },

  afterMove(ctx: MoveContext) {
    const { captured, capturedSq, next } = ctx;
    next.ext![KO] = 0;
    if (!captured) return;
    next.ext![KO] = capturedSq + 1;
    // The capturer never left, and the captured piece changes sides.
    const from = moveFrom(ctx.move);
    const turned = (captured & TYPE_MASK) | colorBit(ctx.us);
    next.board[from] = ctx.piece;
    next.board[capturedSq] = turned;
    if (ctx.rules.isRoyal[ctx.piece & TYPE_MASK]) next.royals[ctx.us] = from;
    next.castling = ctx.prev.castling & CASTLE_MASK[capturedSq];
    if (ctx.events) {
      ctx.events.length = 0;
      ctx.events.push({ t: 'fx', kind: 'flip', sq: capturedSq });
      ctx.events.push({ t: 'add', sq: capturedSq, piece: turned, cause: 'flip' });
    }
  },

  isLegal(ctx: MoveContext) {
    if (ctx.captured && ctx.capturedSq === koSquare(ctx.prev)) return false;
    if (ctx.captured && ctx.rules.isRoyal[ctx.piece & TYPE_MASK]) {
      // Only an unprotected piece: the king must be safe standing where it would capture.
      const b = ctx.prev.board.slice();
      b[moveFrom(ctx.move)] = 0;
      b[ctx.capturedSq] = ctx.piece;
      if (ctx.rules.isAttacked(b, ctx.capturedSq, ctx.them)) return false;
    }
    const promo = movePromo(ctx.move);
    return !promo || !ctx.captured || promo === ctx.rules.promotions[0];
  },
};
